import { DisplayDate } from '@tock/time';
import { LocalDate, LocalDateTime } from '@js-joda/core';
import { PlainButton, Typography } from 'omakase';
import { WaitlistRequestMetadata } from './ExperienceSearchRoute';
import { consumer } from '@tock/coretypes';
import { isEmpty, max, some } from 'es-toolkit/compat';
import { pluralize } from '@tock/shared/utils/pluralize';
import { useBusinessCalendar } from 'hooks/calendar/useBusinessCalendar';

interface Props {
  business?: consumer.IBusinessConfig;
  offerings?: consumer.ConsumerOfferings;
  onShowWaitlist: (experienceId?: number, requestMetadata?: WaitlistRequestMetadata) => void;
}

interface SameDayState {
  isTodaySelected: boolean;
  isAfterCutoff: boolean;
}

export const getSameDayState = (
  currentDateTime: LocalDateTime,
  cutoffTime: string,
  offerings: consumer.ConsumerOfferings,
  selectedDate: string
): SameDayState => {
  const nowDate = currentDateTime.toLocalDate().toString();
  const nowTime = currentDateTime.toLocalTime().toString();
  const isTodaySelected = selectedDate === currentDateTime.toLocalDate().toString();
  // TODO(geoff): handle the non same day cutoff logic for the search modal.
  const isOfferingOpenToday = some(offerings.openDate, (date: string) => date === nowDate);
  const isAfterBusinessCutoff = isTodaySelected && nowTime > cutoffTime;
  const isAfterCutoff = isOfferingOpenToday ? false : isAfterBusinessCutoff;
  return {
    isTodaySelected,
    isAfterCutoff,
  };
};

const ExperienceSearchStatus: React.FunctionComponent<Props> = ({
  business,
  offerings,
  onShowWaitlist,
}) => {
  const {
    selectedDateStats: { openOnSelectedDate },
    partySizeDates: { offeredDates, availableDates, soldDates },
    search: {
      params: { date: selectedDate, partySize: selectedSize },
    },
  } = useBusinessCalendar();

  if (!business) {
    return null;
  }
  if (!offerings) {
    return null;
  }

  const { isTodaySelected, isAfterCutoff } = getSameDayState(
    LocalDateTime.now(),
    business.cutoffTime as string,
    offerings,
    selectedDate
  );

  const nothingOfferedForSize = isEmpty(offeredDates);
  const nothingOfferedForSizeAndDate = !offeredDates?.[selectedDate];
  const soldOutForSizeAndDate =
    offeredDates?.[selectedDate] && soldDates[selectedDate] && !availableDates[selectedDate];
  const acceptsWalkins = business.acceptsWalkins;

  const getStatusInternal = () => {
    if (selectedDate < LocalDate.now().toString()) {
      return (
        <span>
          <Typography color="inherit" component="span" variant="body02">
            {business.name} reservations are no longer available for{' '}
            <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />
          </Typography>
        </span>
      );
    }

    const maxOfferedDate = max(Object.entries(offeredDates || {}).map((entry) => entry[0])) || '';

    if (selectedDate > maxOfferedDate) {
      return (
        <span>
          <Typography color="inherit" component="span" variant="body02">
            {business.name} has not opened reservations for{' '}
            <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />
          </Typography>
        </span>
      );
    }

    if (!openOnSelectedDate) {
      return (
        <span>
          <Typography color="inherit" component="span" variant="body02">
            {business.name} is not offering reservations on{' '}
            <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />
          </Typography>
        </span>
      );
    }

    if (openOnSelectedDate && isTodaySelected && isAfterCutoff) {
      return (
        <span>
          <Typography color="inherit" component="span" variant="body02">
            {business.name} is no longer accepting reservations for{' '}
            <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />
            {acceptsWalkins && <span>, but they also accept walk-ins.</span>}
          </Typography>
        </span>
      );
    }

    if (nothingOfferedForSize) {
      return (
        <span>
          <Typography color="inherit" component="span" variant="body02">
            {business.name} is not offering reservations for {pluralize(selectedSize, 'guest')}. Try
            another size
            {business.showWaitlist ? (
              <span>
                {' or '}
                <PlainButton onClick={() => onShowWaitlist()} size="small" variant="primary">
                  Notify
                </PlainButton>
                .
              </span>
            ) : (
              '.'
            )}
          </Typography>
        </span>
      );
    }

    if (nothingOfferedForSizeAndDate) {
      return (
        <span>
          <Typography color="inherit" component="span" variant="body02">
            {business.name} is not offering reservations for {pluralize(selectedSize, 'guest')} on{' '}
            <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />. Try
            another date
            {business.showWaitlist ? (
              <span>
                {' or '}
                <PlainButton onClick={() => onShowWaitlist()} size="small" variant="primary">
                  Notify
                </PlainButton>
                .
              </span>
            ) : (
              '.'
            )}
          </Typography>
        </span>
      );
    }

    if (soldOutForSizeAndDate) {
      return (
        <Typography color="inherit" component="span" variant="body02">
          {business.name} has sold out all reservations for {pluralize(selectedSize, 'guest')} on{' '}
          <DisplayDate dateValue={selectedDate} displayMonthAs="short" showYear={false} />. Try
          another party size or date
          {business.showWaitlist ? (
            <span>
              {' or '}
              <PlainButton onClick={() => onShowWaitlist()} size="small" variant="primary">
                Notify
              </PlainButton>
              .
            </span>
          ) : (
            '.'
          )}
        </Typography>
      );
    }
    return undefined;
  };

  const statusInternal = getStatusInternal();

  return statusInternal ? (
    <Typography color="inherit" component="span" variant="body02Strong">
      {statusInternal}
    </Typography>
  ) : null;
};

export default ExperienceSearchStatus;
